import { Typography, Box, Paper } from '@mui/material';
import React, { useMemo, useState } from 'react'; // CHANGED: import useMemo here
import * as types from 'application/_types/types.type';
import { GamifyIcon } from 'application/components/GamifyIcon';
import StageSettingsForm from './configurationForms/StageSettingsForm';
import StageConnector from './styledComponents/StageConnector';
// import { getPinsDict } from 'application/routes/config/utils/tempsUtils';
import { StyledButton } from './configurationForms/styledFormComponents';
import StageCard from './styledComponents/StageCard';
import ShrinkButton from './styledComponents/ShrinkButton';
import { updateStage, createStage } from 'graphql/mutations';
import { API, graphqlOperation } from 'aws-amplify';
import { GamifyToast } from 'application/common/CustomToasts';
import ConfirmationDialog from 'application/components/ConfirmationDialog';
import useOrgStore from 'application/store/zustand/useOrgStore';
const { v4: uuidv4 } = require('uuid');

// const pinItems = getPinsDict();

interface StageConfigProps {
  selectedCRM: types.StageCategoryPartial | null;
}

// ADDED: lightweight draft flag to help UI styling (non-breaking)
type StageWithDraft = types.StagePartial & { isDraft?: boolean };

// ADDED: where the draft is intended to go
type PendingOp =
  | { type: 'ADD_AT_END' }
  | { type: 'ADD_IN_GROUP'; anchorStageId: string }
  | { type: 'ADD_BETWEEN'; topStageId: string }; // draft sequence = top.sequence + 1 (virtual new group)

export default function StageConfig({ selectedCRM }: StageConfigProps) {
  const [selectedStage, setSelectedStage] = useState<StageWithDraft | null>(
    null
  );
  const [draftStage, setDraftStage] = useState<StageWithDraft | null>(null); // ADDED: holds the placeholder/new stage
  const [pendingOp, setPendingOp] = useState<PendingOp | null>(null); // ADDED: remembers the intent (end/between/in-group)
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const categories = useOrgStore((state) => state.categories);
  const setCategories = useOrgStore((state) => state.setCategories);
  const stages = selectedCRM?.stages ?? [];

  // Result: array of arrays grouped by sequence (dropping seq < 0)
  const stagesCompiled: types.StagePartial[][] = useMemo(() => {
    const bySeq: Record<number, types.StagePartial[]> = {};
    for (const s of stages ?? []) {
      const seq = (s as any).sequence as number;
      if (typeof seq !== 'number' || seq < 0) continue;
      (bySeq[seq] ??= []).push(s);
    }
    return Object.entries(bySeq)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([, group]) =>
        group.sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
      );
  }, [stages, selectedCRM]);

  // ADDED: compute a version that includes the draft placeholder in the right place
  const compiledWithDraft: StageWithDraft[][] = useMemo(() => {
    if (!draftStage || !pendingOp) return stagesCompiled as StageWithDraft[][];

    // clone a shallow structure we can modify safely
    const clone: StageWithDraft[][] = stagesCompiled.map(
      (g) => g.slice() as StageWithDraft[]
    );

    const findStage = (id: string) =>
      stages.find((s) => s.id === id) as StageWithDraft | undefined;

    const maxSeq = stages.reduce(
      (m, s) => (s.sequence! > m ? s.sequence! : m),
      -1
    );

    if (pendingOp.type === 'ADD_AT_END') {
      // virtual last group (sequence = maxSeq + 1)
      const targetSeq = (maxSeq ?? -1) + 1;
      // when there is no group yet, just create the first virtual group
      clone.push([{ ...draftStage, sequence: targetSeq, isDraft: true }]);
      return clone;
    }

    if (pendingOp.type === 'ADD_IN_GROUP') {
      const anchor = findStage(pendingOp.anchorStageId);
      if (!anchor || typeof anchor.sequence !== 'number') return clone;
      // push the draft into the same group as anchor
      const groupIdx = clone.findIndex((g) =>
        g.some((st) => st.sequence === anchor.sequence)
      );
      if (groupIdx >= 0) {
        clone[groupIdx] = [
          ...clone[groupIdx],
          { ...draftStage, sequence: anchor.sequence, isDraft: true },
        ];
      }
      return clone;
    }

    if (pendingOp.type === 'ADD_BETWEEN') {
      const top = findStage(pendingOp.topStageId);
      if (!top || typeof top.sequence !== 'number') return clone;

      const newSeq = top.sequence + 1; // virtual new group between top and the next group
      // Build a new array and insert a new group *after* the top group index
      const topGroupIdx = clone.findIndex((g) =>
        g.some((st) => st.sequence === top.sequence)
      );
      if (topGroupIdx >= 0) {
        const before = clone.slice(0, topGroupIdx + 1);
        const after = clone.slice(topGroupIdx + 1);
        const newGroup: StageWithDraft[] = [
          { ...draftStage, sequence: newSeq, isDraft: true },
        ];
        // IMPORTANT: we are not shifting real sequences here; this is just a visual/virtual group
        return [...before, newGroup, ...after];
      }
      return clone;
    }

    return clone;
  }, [draftStage, pendingOp, stagesCompiled, stages]);

  // ADDED: small helper to spin up a draft object + open the form
  const createDraft = (sequence: number) => {
    const temp: StageWithDraft = {
      id: 'temp',
      title: 'New Stage',
      description: '',
      isActive: true,
      isDisplay: true,
      requireCloser: false,
      sendToCRM: false,
      userSelectable: true,
      isMapDefault: false,
      changeToDispositionID: '',
      sequence, // place intent
      isDraft: true, // flag for UI
    };
    setDraftStage(temp); // make placeholder appear
    setSelectedStage(temp); // open form with draft
  };

  // ADDED: handlers for the three entry points
  const handleAddAtEnd = () => {
    const maxSeq = stages.reduce(
      (m, s) => (s.sequence! > m ? s.sequence! : m),
      -1
    );
    setPendingOp({ type: 'ADD_AT_END' });
    createDraft((maxSeq ?? -1) + 1);
  };

  const handleAddInGroup = (anchor: types.StagePartial) => {
    setPendingOp({ type: 'ADD_IN_GROUP', anchorStageId: anchor.id as string });
    createDraft(anchor.sequence ?? 0);
  };

  const handleAddBetween = (topOfPair: types.StagePartial) => {
    setPendingOp({ type: 'ADD_BETWEEN', topStageId: topOfPair.id as string });
    createDraft((topOfPair.sequence ?? 0) + 1);
  };

  // ADDED: clear draft helper (used by shrink button or form cancel later)
  const clearDraftSelection = () => {
    setSelectedStage(null);
    setDraftStage(null);
    setPendingOp(null);
  };

  const handleSubmit = async (values: types.StagePartial) => {
    console.log('handleSubmit:', { pendingOp, draftStage, values }); // CHANGED
    if (values.id !== 'temp') {
      await handleUpdateStage(values);
    } else {
      console.log('CREATE (stub):', { pendingOp, draftStage, values }); // CHANGED
      await handleCreateStage(values);
    }
  };
  const handleDeleteStage = () => {
    if (selectedStage?.id !== 'temp') {
      setConfirmationDialogOpen(true);
    } else {
      setSelectedStage(null);
      setDraftStage(null);
      setPendingOp(null);
    }
  };
  const handleUpdateStage = async (values: types.StagePartial) => {
    await API.graphql(graphqlOperation(updateStage, { input: values }));
    const updatedCategory = categories.find(
      (category) => category.id === selectedCRM?.id
    );
    const updatedStages = updatedCategory?.stages?.map((stage) =>
      stage.id === values.id ? { ...stage, ...values } : stage
    );
    const updatedCategoryUpdated = {
      ...updatedCategory,
      stages: updatedStages,
    };
    const updatedCategories = categories.map((category) =>
      category.id === selectedCRM?.id ? updatedCategoryUpdated : category
    );
    setCategories(updatedCategories);
    GamifyToast.success('Stage updated successfully');
    setSelectedStage(null);
  };

  const handleDelete = async () => {
    console.log('using new delete');
    if (!selectedStage || !selectedCRM) {
      console.warn('No stage or CRM selected for deletion');
      return;
    }

    try {
      // Snapshot the current category + stages
      const cat = categories.find((c) => c.id === selectedCRM.id);
      const all = cat?.stages ?? [];

      // Guard: if stage not found in current category, just bail gracefully
      const stageToDelete = all.find((s) => s.id === selectedStage.id);
      if (!stageToDelete) {
        console.warn('Selected stage not found in category');
        return;
      }

      // We only consider visible/ordered stages (sequence >= 0)
      const deletedSeq =
        typeof stageToDelete.sequence === 'number'
          ? stageToDelete.sequence
          : -1;

      // 1) Delete (soft-delete) the stage on the backend
      await API.graphql(
        graphqlOperation(updateStage, {
          input: { id: stageToDelete.id, isDeleted: true },
        })
      );

      // Prepare a working copy of remaining stages (local mirror)
      const remaining = all.filter((s) => s.id !== stageToDelete.id);

      // 2) Decide whether we must renumber
      //    Only renumber if:
      //    - The deleted stage had sequence >= 0 (i.e., was visible/ordered), AND
      //    - It was the ONLY member at that sequence.
      let nextStages = remaining;

      if (deletedSeq >= 0) {
        const stillAtDeletedSeq = remaining.filter(
          (s) => typeof s.sequence === 'number' && s.sequence === deletedSeq
        );

        const wasSoloAtSeq = stillAtDeletedSeq.length === 0;

        if (wasSoloAtSeq) {
          // 3) Shift DOWN by 1 all stages with sequence > deletedSeq (only visible sequences)
          //    Do backend first (ascending is safe for decrements: no collisions)
          const toShift = remaining
            .filter(
              (s) =>
                typeof s.sequence === 'number' &&
                (s.sequence as number) > deletedSeq
            )
            .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

          for (const s of toShift) {
            await API.graphql(
              graphqlOperation(updateStage, {
                input: { id: s.id, sequence: (s.sequence as number) - 1 },
              })
            );
          }

          // Local mirror of shifts (do not touch hidden stages with sequence === -1)
          const shiftedIds = new Set(toShift.map((s) => s.id));
          nextStages = remaining.map((s) =>
            shiftedIds.has(s.id)
              ? { ...s, sequence: (s.sequence as number) - 1 }
              : s
          );
        }
        // else: parallel group remains at deletedSeq → no renumber
      }
      // else: hidden stage (sequence === -1) → no renumber

      // 4) Update local categories state
      const updatedCategory = { ...cat, stages: nextStages };
      const updatedCategories = categories.map((c) =>
        c.id === selectedCRM.id ? updatedCategory : c
      );
      setCategories(updatedCategories);

      // 5) UX cleanup
      setConfirmationDialogOpen(false);
      setSelectedStage(null);
      setDraftStage(null);
      setPendingOp(null);
      GamifyToast.success('Stage deleted successfully');
    } catch (error) {
      console.error('Error deleting stage:', error);
      GamifyToast.error('Error deleting stage');
    }
  };
  const handleCreateStage = async (values: types.StagePartial) => {
    try {
      const all = selectedCRM?.stages ?? [];

      // 1) Decide target sequence (prefer the draft’s sequence)
      let targetSeq =
        typeof draftStage?.sequence === 'number' ? draftStage.sequence! : -1;

      if (targetSeq < 0) {
        // Fallbacks only if draft didn't carry it
        const maxSeq = all.reduce(
          (m, s) =>
            typeof s.sequence === 'number' && s.sequence >= 0 && s.sequence > m
              ? s.sequence
              : m,
          -1
        );
        if (pendingOp?.type === 'ADD_IN_GROUP') {
          const anchor = all.find((s) => s.id === pendingOp.anchorStageId);
          targetSeq =
            typeof anchor?.sequence === 'number' ? anchor.sequence : maxSeq + 1;
        } else if (pendingOp?.type === 'ADD_BETWEEN') {
          const top = all.find((s) => s.id === pendingOp.topStageId);
          targetSeq = (top?.sequence ?? maxSeq) + 1;
        } else {
          targetSeq = maxSeq + 1; // ADD_AT_END default
        }
      }

      // 2) If BETWEEN, shift >= targetSeq on backend first (descending)
      let nextStages = all;
      if (pendingOp?.type === 'ADD_BETWEEN') {
        const toShift = all
          .filter(
            (s) => typeof s.sequence === 'number' && s.sequence >= targetSeq
          )
          .sort((a, b) => (b.sequence ?? 0) - (a.sequence ?? 0));

        for (const s of toShift) {
          await API.graphql(
            graphqlOperation(updateStage, {
              input: { id: s.id, sequence: (s.sequence as number) + 1 },
            })
          );
        }

        // local mirror
        const shiftedIds = new Set(toShift.map((s) => s.id));
        nextStages = all.map((s) =>
          shiftedIds.has(s.id)
            ? { ...s, sequence: (s.sequence as number) + 1 }
            : s
        );
      }

      // 3) Create the new stage at targetSeq
      const newId = uuidv4();
      const createInput: types.StagePartial & { id: string } = {
        ...values,
        id: newId,
        sequence: targetSeq,
        orgID: selectedCRM?.orgID,
        categoryID: selectedCRM?.id,
        isNoDealDefault: false,
        isDeleted: false,
        // categoryID: selectedCRM?.id, // include if your schema needs it
      };

      const res: any = await API.graphql(
        graphqlOperation(createStage, { input: createInput })
      );
      const created: types.StagePartial = res?.data?.createStage ?? createInput;

      // 4) Local state update
      const merged = [...nextStages, created];
      const updatedCategories = categories.map((cat) =>
        cat.id === selectedCRM?.id ? { ...cat, stages: merged } : cat
      );
      setCategories(updatedCategories);

      // 5) UX cleanup
      GamifyToast.success('Stage created successfully');
      setDraftStage(null);
      setPendingOp(null);
      setSelectedStage(created);
    } catch (err) {
      console.error('Error creating stage:', err);
      GamifyToast.error('Error creating stage');
      // keep draft selected so user can retry
    }
  };
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({ p: 2, backgroundColor: theme.custom?.background1 })}
    >
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography
          sx={(theme) => ({
            fontWeight: 780,
            fontSize: 18,
            color: theme.custom?.text,
          })}
        >
          Stage Settings
        </Typography>
        <Typography
          sx={(theme) => ({
            fontWeight: 400,
            fontSize: 15,
            color: theme.custom?.textLight,
          })}
        >
          Personalize and reorder your stages
        </Typography>
      </Box>

      {/* Container for diagram + form */}
      <Box sx={{ display: 'flex', gap: 2, transition: 'all 0.4s ease' }}>
        {/* Diagram box */}
        <Box
          sx={(theme) => ({
            flex: selectedStage ? '0 0 65%' : '1 1 100%',
            transition: 'all 0.4s ease',
            bgcolor: theme.custom?.isDark ? '#1A1A1A' : '#fafafa',
            backgroundImage: theme.custom?.isDark
              ? `radial-gradient(circle, #444 1px, transparent 1px)`
              : `radial-gradient(circle, #ccc 1px, transparent 1px)`,
            border: `2px dashed ${theme.custom?.textLight || '#A2ABB1'}`,
            backgroundSize: '20px 20px',
            borderRadius: 2,
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          })}
        >
          {/* Top-left title + Add Stage */}
          <Box
            sx={{
              p: 2,
              alignSelf: 'flex-start',
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              sx={(theme) => ({
                fontWeight: 700,
                fontSize: 16,
                color: theme.custom?.text,
              })}
            >
              {`Stages (${stages?.length})`}
            </Typography>
            <StyledButton
              startIcon={<GamifyIcon icon="add" backgroundColor="#03252E" />}
              sx={{ maxWidth: '160px' }}
              onClick={handleAddAtEnd}
            >
              Add Stage
            </StyledButton>
          </Box>

          {/* Middle: grouped by sequence (with draft placeholder if any) */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 1,
              px: 2,
            }}
          >
            {compiledWithDraft.map((group, gIdx) => {
              const groupKey =
                (group[0]?.sequence ?? gIdx).toString() +
                '-' +
                group.length +
                '-' +
                group[0]?.id;
              const addConnector = gIdx > 0;
              const isGroup = group.length > 1;

              return (
                <React.Fragment key={groupKey}>
                  {/* Connector ABOVE group (not for the very first group) */}
                  {addConnector && (
                    <StageConnector
                      onClick={() => {
                        // CHANGED: clicking connector drafts a stage *between* previous and this group
                        const prevGroupLast = compiledWithDraft[gIdx - 1]?.[0];
                        if (prevGroupLast) handleAddBetween(prevGroupLast);
                      }}
                    />
                  )}

                  {/* Group container: row of stage cards side by side */}
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      alignItems: 'stretch',
                      justifyContent: 'center',
                      alignSelf: 'center',
                      width: 'fit-content',
                      maxWidth: '100%',
                      bgcolor: isGroup
                        ? theme.custom?.isDark
                          ? 'rgba(53, 130, 158, 0.15)'
                          : 'rgba(201, 231, 238, 0.50)'
                        : 'transparent',
                      borderRadius: isGroup ? 2 : 0,
                      p: isGroup ? 1 : 0,
                      border: isGroup
                        ? `1px solid ${theme.custom?.secondary}`
                        : 'none',
                      boxSizing: 'border-box',
                      overflow: 'visible',
                    })}
                  >
                    {group.map((stage) => (
                      <Box
                        key={stage.id}
                        sx={{
                          flex: '0 0 320px' /* fixed card width */,
                          maxWidth: 320,
                        }}
                      >
                        <StageCard
                          stage={stage as any}
                          selectedStage={selectedStage}
                          onStageClick={setSelectedStage}
                          selectedCRM={selectedCRM}
                          onAddStage={() => handleAddInGroup(stage)}
                        />
                      </Box>
                    ))}
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>

          {/* Bottom-right button */}
          <Box sx={{ p: 2, alignSelf: 'flex-end' }}>
            <ShrinkButton
              disabled={!selectedStage}
              onClick={clearDraftSelection}
            />{' '}
            {/* CHANGED */}
          </Box>
        </Box>

        {/* Form box */}
        <Box
          sx={{
            flex: selectedStage ? '0 0 35%' : '0 0 0%',
            transition: 'all 0.4s ease',
            minHeight: 400,
          }}
        >
          {selectedStage && (
            <StageSettingsForm
              stage={selectedStage}
              selectedCRM={selectedCRM as types.StageCategoryPartial}
              // CHANGED: for now, just log; later wire async create + (optional) reindex
              onSubmit={handleSubmit}
              // In a later step:
              // - if ADD_BETWEEN: call backend to shift + create atomically
              // - replace draft with real stage id, clear pendingOp
              // - setSelectedStage(newStage) OR clearDraftSelection()
              onDelete={handleDeleteStage}
            />
          )}
        </Box>
      </Box>
      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleDelete}
        title={`Are you sure you want to delete the "${selectedStage?.title} stage"? This action cannot be undone.`}
      />
    </Paper>
  );
}
